import Head from 'next/head';
import PropTypes from 'prop-types';
import {
  fetchTopologyInfo,
  fetchTopologyFeed,
  fetchNewSyllabusFeed
} from '@actions/topologies';
import { useSetSelectedNav } from '@stores/sidenav';
import { PlusTopologySource } from '@constants/index';
import SEO from '@seo/index';
import subTopicSchema from '@seo/topic/topic';
import { redirectToRoute } from '@utils/route';
import { getCoursesAsPath } from '@models/Topology';
import PlusTopology from '@cont/PlusTopics/PlusTopology';
import {
  getIsSyllabusV1Enabled,
  getIsTopologySelfDataFetched,
  getTopologyDataByUID
} from '@selectors/topologies';
import {
  getIsTopologyFeedLoaded,
  getTopicFeedRowData
} from '@selectors/syllabus';
import { TOPIC_SYLLABUS_PAGE } from '@constants/segment';
import setupInitialProps from '@utils/setupInitialProps';
import pushRoute from '@utils/pushRoute';
import redirect from '@cont/App/redirect';
import isEmptyObject from '@utils/isEmptyObject';
import Layout from '@layout/index';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';

const PlusTopicPage = ({ goalInfo, topicInfo }) => {
  const {
    name: goalName,
    goalSlug,
    uid: goalUID,
    thumbnail: goalThumbnail
  } = goalInfo || {};
  const {
    uid: topicUID,
    name: topicName,
    thumbnail: topicThumbnail
  } = topicInfo || {};
  const metaTitle = `Prepare for ${goalName || 'Goal'} ${
    topicName || 'Topic'
  } with Top Educators on Unacademy`;
  const metaDescription = `Join ${goalName || 'Goal'} ${
    topicName || 'Topic'
  } course on Unacademy conducted by Top
  Educators. Learners can also get access to curated chemistry course and previous years &amp;
  mock questions.`;
  const canonicalURL = `https://unacademy.com/goal/${goalSlug}/${goalUID}/topic/${topicUID}`;

  useSetSelectedNav('topics');

  return (
    <>
      <SEO
        title={metaTitle}
        description={metaDescription}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
          images: [
            {
              url: topicThumbnail || goalThumbnail
            }
          ],
          url: canonicalURL
        }}
        schema={subTopicSchema({
          goal: goalInfo,
          topic: { name: topicName, permalink: canonicalURL }
        })}
      />
      <Head>
        <title>{metaTitle}</title>
      </Head>
      <PlusTopology
        source={PlusTopologySource.TOPIC_FEED}
        goalUID={goalUID}
        topologyUID={topicUID}
      />
    </>
  );
};

PlusTopicPage.pageInfo = {
  isMWebSupported: true,
  name: TOPIC_SYLLABUS_PAGE,
  layoutOptions: {
    showPlusNav: true
  }
};

export default PlusTopicPage;

PlusTopicPage.getInitialProps = setupInitialProps(
  async ({ store, query, isServer, res }) => {
    const { goalUID, topicUid, goalSlug } = query;

    if (goalUID === 'undefined' || topicUid === 'undefined')
      // Clearing out incorrect URLs from sitemap
      return redirect(
        isServer,
        { as: '/404', href: '/404' },
        { res },
        {},
        true
      );

    if (goalUID && topicUid) {
      const promises = [];
      let storeState = store.getState();
      if (!getIsTopologySelfDataFetched(storeState, goalUID))
        promises.push(store.dispatch(fetchTopologyInfo(goalUID)));
      if (!getIsTopologySelfDataFetched(storeState, topicUid))
        promises.push(store.dispatch(fetchTopologyInfo(topicUid)));
      if (!getIsTopologyFeedLoaded(storeState, topicUid))
        promises.push(
          store.dispatch(
            fetchTopologyFeed({
              topologyUid: topicUid,
              pageSource: PlusTopologySource.TOPIC_FEED
            })
          )
        );
      await Promise.all(promises);

      storeState = store.getState();
      const topologyInfo = getTopologyDataByUID(goalUID)(storeState);
      if (isEmptyObject(topologyInfo))
        return redirect(isServer, { as: '/404', href: '/404' }, { res });

      const isNewSyllabusEnabled = getIsSyllabusV1Enabled(storeState, goalUID);
      if (isNewSyllabusEnabled) {
        const newSyllabusData = await store.dispatch(
          fetchNewSyllabusFeed(goalUID)
        );
        if (newSyllabusData.result?.data?.find((el) => el.uid === topicUid)) {
          if (isServer) {
            res.writeHead(302, {
              Location: `/goal/${goalSlug}/${goalUID}/topics`
            });
            return res.end();
          }

          return pushRoute(
            {
              pathname: `/goal/${goalSlug}/${goalUID}/topics`,
              query
            },
            `/goal/${goalSlug}/${goalUID}/topics`
          );
        }
      }
      const topologyChildren = getTopicFeedRowData(storeState, topicUid);
      if (topologyChildren.length === 1) {
        const topic = topologyChildren[0];
        if (topic.uid)
          return redirectToRoute(
            isServer,
            res,
            getCoursesAsPath(goalSlug, goalUID, topic.uid)
          );
      } else if (topologyChildren.length === 0)
        return redirectToRoute(
          isServer,
          res,
          getCoursesAsPath(goalSlug, goalUID, topicUid)
        );
    }

    return {
      goalInfo: store.getState().topologies.data[goalUID],
      topicInfo: store.getState().topologies.data[topicUid]
    };
  },
  PlusTopicPage.pageInfo
);

PlusTopicPage.propTypes = {
  goalInfo: PropTypes.objectOf(PropTypes.any).isRequired,
  topicInfo: PropTypes.objectOf(PropTypes.any).isRequired
};
PlusTopicPage.Layout = Layout;
PlusTopicPage.Wrapper = PageReduxProvider;
